import React, {
  FunctionComponent, ReactElement,
} from 'react';
import { StylesConfig } from 'react-select';
import { IData } from '../../types/dropdown';
import Divider from '../divider/Divider';
import Dropdown from '../dropdown/Dropdown';
import Typography from '../typography/Typography';
import './popUp.scss';

type PopUpItem = {
  label: string,
  icon: ReactElement,
  onClick: () => void,
}

type PopUpProps = {
  isOpen: boolean,
  togglePopUp: () => void,
  items: PopUpItem[],
  top?: number,
  left?: number,
  className?: string
  itemClassName?: string,
  langSelect?: boolean,
  onLanguageChange?: (langValue: IData) => void;
  langOptions?: IData[],
  langValue?: string,
  langDefaultValue?: string,
  noOverlay?: boolean;
}

const languageSelectStyles: StylesConfig<any, false> = {
  control: (css) => ({
    ...css,
    height: '64px',
    padding: '0',
    backgroundColor: '#fff',
    boxShadow: 'none',
    border: 'none',
    borderBottom: '1px solid #dfe2e5',
    borderRadius: 'none',
    borderColor: '#dfe2e5s',
    '&:hover': {
      borderColor: '#dfe2e5s',
    },
  }),
  indicatorsContainer: (css) => ({
    ...css,
    order: 1,
    border: 'none',
  }),
  container: (css) => ({
    ...css,
    position: 'relative',
  }),
  menu: (css) => ({
    ...css,
    width: '100%',
    backgroundColor: 'white',
    maxHeight: 180,
    position: 'static',
    boxShadow: 'none',
  }),
  menuList: (css) => ({
    ...css,
    maxHeight: 180,
    paddingTop: 0,
    border: 'none',
    margin: '0',
    '::-webkit-scrollbar': {
      width: '4px',
      height: '0px',
    },
    '::-webkit-scrollbar-track': {
      background: '#fff',
    },
    '::-webkit-scrollbar-thumb': {
      background: '#C4C4C4',
      borderRadius: '8px',
    },
    '::-webkit-scrollbar-thumb:hover': {
      background: '#555',
    },
  }),
  option: (css, state) => ({
    ...css,
    display: 'flex',
    alignItems: 'center',
    fontSize: '16px',
    lineHeight: '24px',
    padding: '10px 0 10px 10px',
    color: '#38414a',
    backgroundColor: state.isFocused || state.isSelected ? '#e6f4f8' : '#fff',
    '&:hover': {
      borderRadius: '4px',
    },
    cursor: 'pointer',
    borderRadius: state.isSelected ? '8px' : 'none',
    'span:nth-child(2)': {
      fontWeight: state.isSelected ? 600 : 400,
    },
  }),
  placeholder: (css, state) => ({
    ...css,
    color: state.isDisabled ? '#dfe2e5' : '#7e8a96',
  }),
  singleValue: (css) => ({
    ...css,
    '.icon-container': {
      marginLeft: '0px',
    },
  }),
  valueContainer: (css) => ({
    ...css,
    paddingLeft: '0px',
  }),
};

const PopUp: FunctionComponent<PopUpProps> = ({
  isOpen,
  togglePopUp,
  items,
  top = 72,
  left = 16,
  className = '',
  itemClassName = '',
  langSelect,
  onLanguageChange,
  langOptions,
  langValue,
  langDefaultValue,
  noOverlay = false,
}) => (
  <>
    <div className={`pop-up-overlay ${isOpen && !noOverlay ? 'active' : ''}`} onClick={togglePopUp} />
    <div
      className={`pop-up p-v-15 p-h-16 ${className} ${isOpen ? 'open' : ''}`}
      style={{
        top: `${top}px`,
        left: `${left}px`,
        right: `${left}px`,
      }}
    >
      {items.map((item: PopUpItem) => (
        <div data-testid={`popup-item_${item.label.replace(/\s/g, '-').toLowerCase()}`} key={item.label}>
          <div
            onClick={() => {
              togglePopUp();
              item.onClick();
            }}
            className={`pop-up-list-item d-flex align-items-center p-v-19 pointer ${itemClassName}`}
          >
            {item.icon}
            <Typography variant="h6" className="m-l-17">{item.label}</Typography>
          </div>
          <Divider />
        </div>
      ))}
      {langSelect && (
      <Dropdown
        value={langValue}
        onChange={onLanguageChange}
        data={langOptions || []}
        className="pop-up-language-select"
        styles={languageSelectStyles}
        defaultValue={langDefaultValue}
        closeMenuOnSelect={false}
      />
      )}
    </div>
  </>
);

export default PopUp;
